import { useContext, useState } from "react";
import { observer } from "mobx-react-lite";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import LoadingSpinner from "@/components/LoadingSpinner";
import { ParametersPreviewContent } from "./AdvancedParameters/ParametersPreview";
import { ParametersContent } from "./ParametersContent/ParametersContent";
import { ParametersTrigger } from "./ParametersTrigger";
import { ParameterProvider } from "./store";
import { ModelParametersProps } from "./types";
import { ModelsStoreProvider } from "@/providers/ProviderAndModelsProvider/models-store";
import { PlaygroundStoreContext } from "@/components/Playground2/playground-store";
import Functions from "@/components/PromptBlueprint/components/Functions";

export const ModelParameters = observer(
  ({
    promptData,
    className,
    onUpdate,
    isPromptChat,
    displayAsPopover = true,
    shouldShowClearButton = true,
    readonly = false,
    showLearnMore = true,
    titleClassName,
    triggerLabel,
    triggerLogo,
    triggerVariant = "ghost",
    buttonSize = "sm",
    showIcon = true,
    showTooltip = true,
    showGeneralInformation = true,
  }: ModelParametersProps) => {
    const [open, setOpen] = useState(false);
    const playground = useContext(PlaygroundStoreContext);

    if (!promptData) {
      return (
        <div className="flex h-full w-[132px] items-center justify-center">
          <LoadingSpinner size={4} />
        </div>
      );
    }

    return (
      <ModelsStoreProvider>
        <ParameterProvider
          displayAsPopover={displayAsPopover}
          isPlayground={!!playground}
          promptData={promptData}
          isPromptChat={isPromptChat}
          isOpen={open}
          onUpdate={onUpdate}
        >
          <div className={`flex ${className} gap-2`}>
            {displayAsPopover ? (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  asChild
                >
                  <ParametersTrigger
                    triggerLabel={triggerLabel}
                    triggerLogo={triggerLogo}
                    triggerVariant={triggerVariant}
                    buttonSize={buttonSize}
                    showIcon={showIcon}
                    showTooltip={showTooltip}
                  />
                </PopoverTrigger>
                <PopoverContent
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={
                    readonly
                      ? "w-[450px] overflow-visible p-4"
                      : "w-full max-w-xl"
                  }
                >
                  {readonly ? (
                    <ParametersPreviewContent
                      showLearnMore={showLearnMore}
                      titleClassName={titleClassName}
                      showGeneralInformation={showGeneralInformation}
                    />
                  ) : (
                    <ParametersContent
                      shouldShowClearButton={shouldShowClearButton}
                    />
                  )}
                </PopoverContent>
              </Popover>
            ) : readonly ? (
              <ParametersPreviewContent
                showLearnMore={showLearnMore}
                titleClassName={titleClassName}
                showGeneralInformation={showGeneralInformation}
              />
            ) : (
              <ParametersContent
                shouldShowClearButton={shouldShowClearButton}
              />
            )}
          </div>
          {playground && <Functions readonly={playground?.readonly} />}
        </ParameterProvider>
      </ModelsStoreProvider>
    );
  },
);
